import React, { useMemo } from 'react';

import { SearchIcon } from '@vlabs/icons';
import { AddedSourcesGroup } from '@vlabs/shared/components/filters-panel/common/source-select-popover/components/AddedSourcesGroup';
import { NoSourceGroup } from '@vlabs/shared/components/filters-panel/common/source-select-popover/components/NoSourceGroup';
import { useSourceSearch } from '@vlabs/shared/components/filters-panel/common/source-select-popover/hooks/useSourceSearch';
import type { TUiStreamGroup } from '@vlabs/shared/components/filters-panel/common/source-select-popover/types';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { useTranslation } from 'react-i18next';

import { useStreamGroupsQuery } from '@vlabs/monokit/entities/events/api';
import { GroupedCheckbox } from '@vlabs/monokit/shared/ui/grouped-checkbox';

import st from './SourceGroupedCheckbox.module.sass';

interface SourceGroupedCheckboxProps {
  selectedSources: string[];
  onChangeSelectedSource: (next: string[]) => void;
  customSourceNames: string[];
  onChangeCustomSourceNames: (next: string[]) => void;
  testId?: string;
}

function SourceGroupedCheckbox({
  selectedSources,
  onChangeSelectedSource,
  customSourceNames,
  onChangeCustomSourceNames,
  testId = 'SourceGroupedCheckbox',
}: SourceGroupedCheckboxProps): React.JSX.Element {
  const { t } = useTranslation();
  const { data: groupsData } = useStreamGroupsQuery();

  const uiGroups = useMemo<TUiStreamGroup[]>(() => (groupsData ?? []).map((group) => ({
    id: group.id,
    label: group.label,
    defaultCollapsed: false,
    items: group.items.map((item) => ({ id: item.value, value: item.value, label: item.label })),
  })), [groupsData]);

  const streams = useMemo(() => uiGroups.flatMap((g) => g.items.map((i) => ({
    name: i.label,
    stream_id: i.value,
    groupsRaw: [g.label],
    statusRaw: '',
  }))), [uiGroups]);

  const groups = useMemo(() => uiGroups.map((g) => ({ group_name: g.label, group_id: g.id })), [uiGroups]);

  const {
    searchValue,
    setSearchValue,
    visibleUiGroups,
    visibleCustomSourceNames,
    canAdd,
    onAdd,
  } = useSourceSearch({
    uiGroups,
    streams,
    groups,
    selectedSources,
    customSourceNames,
    onChangeSelectedSource,
    onChangeCustomSourceNames,
  });

  return (
    <div
      className={st.Content}
      data-testid={testId}
    >
      <div className={st.Toolbar}>
        <Input
          className={st.SearchInput}
          icon={<SearchIcon />}
          id="source_search"
          isClearable
          name="source_search"
          onChange={(e) => setSearchValue(e.target.value)}
          onClear={() => setSearchValue('')}
          size="small"
          value={searchValue}
        />
        <Button
          disabled={!canAdd}
          onClick={onAdd}
          size="small"
          variant="flat"
        >
          {t('btn.add')}
        </Button>
      </div>

      <div className={st.ScrollArea}>
        <div className={st.Sections}>
          <AddedSourcesGroup
            customSourceNames={customSourceNames}
            onChangeCustomSourceNames={onChangeCustomSourceNames}
            onChangeSelectedSource={onChangeSelectedSource}
            selectedSources={selectedSources}
            visibleCustomSourceNames={visibleCustomSourceNames}
          />

          <div className={st.GroupsList}>
            {visibleUiGroups.map((group) => (
              <GroupedCheckbox
                group={group}
                key={group.id}
                onChange={onChangeSelectedSource}
                value={selectedSources}
              />
            ))}
          </div>

          {!searchValue.trim() && (
            <NoSourceGroup
              onChangeSelectedSource={onChangeSelectedSource}
              selectedSources={selectedSources}
            />
          )}
        </div>
      </div>

      <Button
        kind="negative"
        onClick={() => onChangeSelectedSource([])}
        size="small"
        variant="filled"
      >
        {t('button.clear')}
      </Button>
    </div>
  );
}

export { SourceGroupedCheckbox };
